import { useParticipants } from '@livekit/components-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { isTranscriberIdentity } from '../transcript/transcriptProtocol';
import { displayNameOf, initialsOf, toneFor } from './participantDisplay';

function Avatar({ name, identity }) {
    return (
        <span
            aria-hidden="true"
            className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white', toneFor(identity ?? name))}
        >
            {initialsOf(name)}
        </span>
    );
}

/**
 * A aba "Participantes" do painel da chamada: quem está esperando para entrar (com Aprovar e Recusar, a mesma decisão do
 * aviso do alto) e quem já está na chamada. O transcritor da Jev está na sala mas não é uma pessoa: não aparece.
 *
 * @param {{ waiting?: Array<{ participantId: string, displayName: string }>, onAdmit: (guest: object) => void,
 *   onReject: (guest: object) => void, busy?: boolean }} props
 */
export function ParticipantsPanel({ waiting = [], onAdmit, onReject, busy = false }) {
    const people = useParticipants().filter((participant) => !isTranscriberIdentity(participant.identity));

    return (
        <div data-participants-panel="" className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto p-4">
            {waiting.length > 0 && (
                <section aria-label="Aguardando entrada">
                    <h3 className="mb-2 text-[11px] font-bold uppercase tracking-wider text-neutral-400">
                        {`Aguardando entrada (${waiting.length})`}
                    </h3>
                    <ul className="space-y-2">
                        {waiting.map((guest) => (
                            <li key={guest.participantId} className="flex items-center gap-3 rounded-xl bg-white/5 p-2.5">
                                <Avatar name={guest.displayName} identity={guest.participantId} />
                                <span className="min-w-0 flex-1">
                                    <span className="block truncate text-sm font-medium">{guest.displayName}</span>
                                    <span className="block text-xs text-neutral-400">Quer entrar na chamada</span>
                                </span>
                                <span className="flex shrink-0 gap-1">
                                    <Button size="sm" variant="ghost" className="text-neutral-200 hover:bg-white/10 hover:text-white" disabled={busy} onClick={() => onReject(guest)}>
                                        Recusar
                                    </Button>
                                    <Button size="sm" disabled={busy} onClick={() => onAdmit(guest)}>Aprovar</Button>
                                </span>
                            </li>
                        ))}
                    </ul>
                </section>
            )}

            <section aria-label="Na chamada">
                <h3 className="mb-2 text-[11px] font-bold uppercase tracking-wider text-neutral-400">{`Na chamada (${people.length})`}</h3>
                <ul className="space-y-2">
                    {people.map((participant) => {
                        const name = displayNameOf(participant, participant.isLocal ? 'Você' : 'Participante');
                        return (
                            <li key={participant.identity} className="flex items-center gap-3 p-1.5">
                                <Avatar name={name} identity={participant.identity} />
                                <span className="min-w-0 flex-1 truncate text-sm font-medium">
                                    {name}
                                    {participant.isLocal && <span className="font-normal text-neutral-400">{' (você)'}</span>}
                                </span>
                            </li>
                        );
                    })}
                </ul>
            </section>
        </div>
    );
}
